import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, Clock, Info, Sparkles, Award, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ContestCountdown } from './ContestCountdown';
import { ContestRulesModal } from './ContestRulesModal';
import BarIlanApplicationDialog from '@/components/BarIlanApplicationDialog';
import { useFoundingContestState } from '@/hooks/useFoundingContestState';
import AddFriendsModal from '@/components/friends/AddFriendsModal';

interface FoundingContestCardProps {
  userId: string;
  userTrustScore: number;
  networkSize: number;
  bottomPadding?: number;
}

export function FoundingContestCard({
  userId,
  userTrustScore,
  networkSize,
  bottomPadding = 0,
}: FoundingContestCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contestState, uiState, isLoading, refetch } = useFoundingContestState();
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  if (isLoading || !contestState || !uiState) {
    return (
      <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border-2 border-pink-300/50 dark:border-pink-500/30 rounded-2xl sm:rounded-3xl p-4 sm:p-5 md:p-4 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-pink-500/10 profile-card animate-pulse">
        <div className="h-32 flex items-center justify-center">
          <div className="text-sm text-[#9B9490] dark:text-white/60">{t('profile.loadingContest')}</div>
        </div>
      </div>
    );
  }

  const isEligible = userTrustScore > 30;

  return (
    <>
      <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border-2 border-pink-300/50 dark:border-pink-500/30 rounded-2xl sm:rounded-3xl p-4 sm:p-5 md:p-4 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-pink-500/10 profile-card transition-all duration-300 hover:border-pink-400 dark:hover:border-pink-500/50">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <p className="text-sm font-semibold text-[#2D2A26] dark:text-white">
                {t('profile.foundingContest')}
              </p>
            </div>
            <p className="text-xs text-[#9B9490] dark:text-white/60">
              {uiState.isPrelaunch && t('profile.applicationsOpen')}
              {uiState.isRunning && t('profile.contestLive')}
              {uiState.isClosed && t('profile.contestEnded')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowRulesModal(true)}
            className="rounded-full hover:bg-[#F8F4F1] active:bg-[#F0EAE5] dark:hover:bg-white/10 dark:active:bg-white/15 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={t('modals.founding100.info.ariaLabel')}
          >
            <Info className="w-7 h-7 text-[#7C3AED] dark:text-pink-400" />
          </button>
        </div>

        {/* User Stats */}
        <div className="mb-3 space-y-1 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-[#6B6560] dark:text-white/60">{t('profile.yourTrustScore')}</span>
            <span className="font-semibold text-[#7C3AED]">{userTrustScore}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6B6560] dark:text-white/60">{t('profile.networkSize')}</span>
            <span className="font-medium text-[#2D2A26] dark:text-white/90">
              {t('profile.networkSizeConnections', { count: networkSize })}
            </span>
          </div>
        </div>

        {/* PHASE: PRELAUNCH */}
        {uiState.isPrelaunch && (
          <div className="space-y-3">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B6560] dark:text-white/70 font-medium">
                  {t('profile.applicationsProgress', {
                    applied: contestState.applicationsCount,
                    threshold: contestState.threshold,
                  })}
                </span>
                <span className="text-[#7C3AED] font-bold">
                  {uiState.progressPercent}%
                </span>
              </div>
              <div className="w-full bg-[#F0EAE5] dark:bg-white/10 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-pink-500 to-purple-500 h-2.5 rounded-full transition-all duration-500 shadow-lg shadow-pink-500/50"
                  style={{ width: `${Math.max(uiState.progressPercent, 2)}%` }}
                />
              </div>
            </div>

            {/* Application Status or CTA */}
            {isEligible ? (
              <div className="space-y-2">
                {uiState.hasApplied ? (
                  <div
                    className={`rounded-lg border p-3 ${uiState.isApproved
                      ? 'border-green-500/30 bg-green-500/10'
                      : uiState.isRejected
                        ? 'border-red-500/30 bg-red-500/10'
                        : 'border-yellow-500/30 bg-yellow-500/10'
                      }`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      {uiState.isApproved ? (
                        <>
                          <Sparkles className="w-4 h-4 text-green-500" />
                          <span className="text-xs font-semibold text-green-500">
                            Approved — you're eligible 🎉
                          </span>
                        </>
                      ) : uiState.isRejected ? (
                        <>
                          <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-xs font-semibold text-red-400">Not Approved</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />
                          <span className="text-xs font-semibold text-yellow-500">
                            {uiState.isWaitlist ? 'Waitlisted' : t('profile.underReview')}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6B6560] dark:text-white/70 mb-1">
                      {uiState.isApproved
                        ? "You're in the eligible pool. When the contest starts, your Trust Score determines your rank."
                        : uiState.isWaitlist
                          ? 'You are on the waitlist. We will contact you if a spot opens up.'
                          : uiState.isRejected
                            ? 'This application was not selected. Continue building your Trust Score.'
                            : t('profile.reviewWithin48Hours')}
                    </p>
                    {uiState.isApproved && (
                      <p className="text-[10px] text-[#9B9490] dark:text-white/50 italic">
                        💡 Boost your Trust Score by connecting trusted friends.
                      </p>
                    )}
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={() => setShowApplicationDialog(true)}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white text-xs font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
                  >
                    {t('profile.applyToFounding100')}
                  </Button>
                )}

                {/* Invite CTA - helps unlock contest faster */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-pink-300 dark:border-pink-500/30 text-xs text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20"
                  onClick={() => setIsInviteOpen(true)}
                >
                  <Share2 className="w-3.5 h-3.5 me-1.5" />
                  {t('profile.inviteFriendsToUnlockContest')}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-gray-300 dark:border-white/20 text-xs text-gray-800 dark:text-white/90"
                  onClick={() => navigate('/bar-ilan/dashboard')}
                >
                  {t('profile.viewLiveLeaderboard')} →
                </Button>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-[#F8F4F1] dark:bg-black/30 border border-[#E8E0DA]/50 dark:border-white/15">
                <p className="text-[11px] text-[#6B6560] dark:text-white/70">
                  {t('profile.eligibleForFounders')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* PHASE: RUNNING */}
        {uiState.isRunning && contestState.endAt && (
          <div className="space-y-3">
            {/* Countdown Timer */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-pink-200 dark:border-pink-500/30">
              <p className="text-xs font-medium text-gray-700 dark:text-white/80 mb-3 text-center">
                Contest Ends In
              </p>
              <ContestCountdown endAt={contestState.endAt} className="justify-center" />
            </div>

            {/* User Rank (if approved) */}
            {uiState.isApproved && uiState.userRank !== null && (
              <div
                className={`rounded-lg border p-3 ${uiState.isWinner
                  ? 'border-yellow-500/50 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20'
                  : 'border-gray-300 dark:border-white/20 bg-gray-50 dark:bg-white/5'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {uiState.isWinner ? (
                      <Award className="w-5 h-5 text-yellow-500" />
                    ) : (
                      <Users className="w-5 h-5 text-gray-500" />
                    )}
                    <span className="text-xs font-semibold text-gray-700 dark:text-white/90">
                      Your Rank
                    </span>
                  </div>
                  <span
                    className={`text-2xl font-bold ${uiState.isWinner ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-700 dark:text-white'
                      }`}
                  >
                    #{uiState.userRank}
                  </span>
                </div>
                {uiState.isWinner && (
                  <p className="text-[11px] text-yellow-700 dark:text-yellow-300 mt-2">
                    🎉 You're currently in the top 100! Keep your position to win 3 years of verified status.
                  </p>
                )}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-300 dark:border-white/20 text-xs text-gray-800 dark:text-white/90"
              onClick={() => navigate('/bar-ilan/dashboard')}
            >
              {t('profile.viewLiveLeaderboard')} →
            </Button>
          </div>
        )}

        {/* PHASE: CLOSED */}
        {uiState.isClosed && (
          <div className="space-y-3">
            {uiState.isWinner ? (
              <div className="rounded-lg border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-sm font-bold text-yellow-700 dark:text-yellow-300">
                      🎉 Congratulations!
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      You finished #{uiState.userRank}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-yellow-700 dark:text-yellow-300">
                  You won 3 years of verified status (worth $360)! Your prize will be granted within 48 hours.
                </p>
              </div>
            ) : uiState.isApproved && uiState.userRank !== null ? (
              <div className="rounded-lg border border-gray-300 dark:border-white/20 bg-gray-50 dark:bg-white/5 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-6 h-6 text-gray-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-700 dark:text-white/90">
                      Contest Ended
                    </p>
                    <p className="text-xs text-gray-600 dark:text-white/70">
                      You finished #{uiState.userRank}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-gray-600 dark:text-white/70">
                  Thank you for participating! Only the top 100 won the prize this time.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-300 dark:border-white/20 bg-gray-50 dark:bg-white/5 p-4">
                <p className="text-xs text-gray-600 dark:text-white/70 text-center">
                  {t('profile.contestEndedAnnouncement')}
                </p>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-300 dark:border-white/20 text-xs text-gray-800 dark:text-white/90"
              onClick={() => navigate('/bar-ilan/dashboard')}
            >
              {t('profile.viewFinalResults')}
            </Button>
          </div>
        )}
      </div>

      {/* Modals */}
      <ContestRulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

      <BarIlanApplicationDialog
        isOpen={showApplicationDialog}
        onClose={() => setShowApplicationDialog(false)}
        onSuccess={() => {
          setShowApplicationDialog(false);
          void refetch(); // Refresh contest state
        }}
        userId={userId}
        userTrustScore={userTrustScore}
        networkSize={networkSize}
      />

      {/* Invite Friends Modal */}
      <AddFriendsModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        user={{ id: userId }}
      />
    </>
  );
}

